<script>
import Form from '@classes/Form'
import ComboBox from '@components/combo-box.vue'
import { authComputed, authMethods, optionsComputed } from '@state/helpers'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import moment from 'moment'
import { conditionService } from '@services/ConditionService'
import { symptomService } from '@services/SymptomService'
import { healthRecordService } from '@services/HealthRecordService'
import store from '@state/store'
import hrMixin from '@src/mixins/hr-mixin.vue'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
import ColoredRangeSlider from '@/src/components/colored-range-slider.vue'

export default {
  page: {
    title: 'Symptomcheaker Editor',
    meta: [{ name: 'description', content: 'The Condition Editor page.' }],
  },
  components: {
    ComboBox,
    MedicalDatePickerDialog,
    ColoredRangeSlider,
  },
  mixins: [hrMixin],
  props: {
    formDialog: {
      type: Boolean,
      default: false,
    },
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'condition',
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      row: '',
      conditionForm: new Form({
        condition: '',
        verification_status: '',
        start: null,
        start_date_part: '',
        end: null,
        end_date_part: '',
        note: '',
        private: false,
      }),
      symptomForm: new Form({
        symptom: '',
        start: null,
        start_date_part: '',
        end: null,
        end_date_part: '',
        intensity: 1,
        frequency: '',
        interval: '',
        note: '',
        private: false,
      }),
      medicalDateStartPickerDialog: false,
      medicalDateEndPickerDialog: false,
      conditionError: null,
      symptomAttachments: [],
      originalSymptomAttachments: [],
      conditionAttachments: [],
      originalConditionAttachments: [],
      symptomIntensityMin: 1,
      symptomIntensityMax: 10,
      slider: 5,
      rules: {
        intensityRule: (value) => {
          if (value < 1 || value > 10) {
            return this.$t('health-record.conditions.intensity-error')
          }
          return true
        },
        frequencyRule: (value) => {
          if (value) {
            if (typeof value === 'string') {
              if (!value.trim()) return true
            }
            if (!isNaN(parseInt(value)) && value >= 1 && value <= 100) {
              return true
            }
            return this.$t('health-record.conditions.frequency-error')
          }
          return true
        },
      },
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    verificationStatus() {
      return this.getConditionOptions?.verification_status
        ? this.getConditionOptions.verification_status
        : []
    },
    frequencies() {
      return this.getSymptomOptions?.frequency
        ? this.getSymptomOptions.frequency
        : []
    },
    intervals() {
      return this.getSymptomOptions?.interval
        ? this.getSymptomOptions.interval
        : []
    },
    differenceHumanCondition() {
      if (
        this.conditionForm.start &&
        this.conditionForm.end &&
        !isBeforeDate(this.conditionForm.start, this.conditionForm.end)
      ) {
        return moment
          .duration(
            moment(this.conditionForm.end).diff(this.conditionForm.start)
          )
          .humanize()
      }
      return null
    },
    differenceHumanSymptom() {
      if (
        this.symptomForm.start &&
        this.symptomForm.end &&
        !isBeforeDate(this.symptomForm.start, this.symptomForm.end)
      ) {
        return moment
          .duration(moment(this.symptomForm.end).diff(this.symptomForm.start))
          .humanize()
      }
      return null
    },
    canSaveCondition() {
      if (
        (this.conditionForm.start ||
          this.conditionForm.start_date_part === 'past') &&
        this.row === 'condition' &&
        this.conditionForm.condition &&
        this.conditionForm.verification_status &&
        this.conditionError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    canSaveSymptom() {
      if (
        (this.symptomForm.start ||
          this.symptomForm.start_date_part === 'past') &&
        this.row === 'symptom' &&
        this.symptomForm.symptom &&
        this.conditionError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    computedRecord: {
      get() {
        if (this.row === 'condition') {
          return this.conditionForm.private
        } else {
          return this.symptomForm.private
        }
      },
      set(value) {
        if (this.row === 'condition') {
          this.conditionForm.private = value
        } else {
          this.symptomForm.private = value
        }
      },
    },
  },
  watch: {
    conditionForm: {
      handler(newVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    symptomForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
    'symptomForm.interval': function(newVal, oldVal) {
      if (newVal === null && oldVal !== '') {
        this.symptomForm.frequency = ''
      }
    },
    row(newValue, oldValue) {
      if (newValue === 'condition') {
        this.symptomForm.reset()
        this.symptomAttachments = []
      } else {
        this.conditionForm.reset()
        this.conditionAttachments = []
      }
      this.conditionError = null
      this.dateError = null
    },
  },
  async mounted() {
    try {
      if (!this.getConditionOptions) {
        const conditionOptions = (await conditionService.options()).data
        if (conditionOptions) {
          store.dispatch('options/setConditionOptions', conditionOptions)
        }
      }

      if (!this.getSymptomOptions) {
        const symptomOptions = (await symptomService.options()).data
        if (symptomOptions) {
          store.dispatch('options/setSymptomOptions', symptomOptions)
        }
      }

      if (this.edit) {
        this.row = this.item.record_type
        this.itemToForm(this.item)
      }
    } catch (error) {
      console.warn(error)
    }
  },
  methods: {
    ...authMethods,
    clearForm() {
      this.symptomForm.reset()
      this.conditionForm.reset()
      this.symptomAttachments = []
      this.conditionAttachments = []
      this.message = false
      this.isAnother = false
      this.isContinue = false
      this.conditionError = null
      this.dateError = null
    },
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      const fieldPart = this.medicalDatePickerName + '_date_part'

      const medicalDateObject = medicalDateFormatter(medicalDate)

      if (this.row === 'condition') {
        this.conditionForm[field] = medicalDateObject.date
        this.conditionForm[fieldPart] = this.determinePast(
          past,
          medicalDateObject
        )
      } else {
        this.symptomForm[field] = medicalDateObject.date
        this.symptomForm[fieldPart] = this.determinePast(
          past,
          medicalDateObject
        )
      }
      this.medicalDateStartPickerDialog = false
      this.medicalDateEndPickerDialog = false
    },
    async getConditionResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.conditionForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await conditionService.create(
            this.conditionForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async getSymptomResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.symptomForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await symptomService.create(this.symptomForm.removeEmptyFromData())
        ).data
      }
    },
    async deleteAttachmentCondition(id) {
      try {
        const response = await conditionService.attachmentDelete(id)
        if (response) {
          this.originalConditionAttachments = this.originalConditionAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    async deleteAttachmentSymptom(id) {
      try {
        const response = await symptomService.attachmentDelete(id)
        if (response) {
          this.originalSymptomAttachments = this.originalSymptomAttachments.filter(
            function(obj) {
              return obj.id !== id
            }
          )
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      if (this.row === 'condition') {
        this.conditionForm.onFail(error.response.data)
      } else {
        this.symptomForm.onFail(error.response.data)
      }
      if (this.hasResponse) {
        this.message = true
        this.$emit('update:formSaved', true)
      }
      this.conditionError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('conditionError', action)
      try {
        let conditionResponse = null
        let symptomResponse = null
        if (this.row === 'condition') {
          if (
            this.conditionForm.start &&
            isBeforeDate(
              this.conditionForm.start,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          if (
            this.conditionForm.start &&
            this.conditionForm.end &&
            isBeforeDate(this.conditionForm.start, this.conditionForm.end)
          ) {
            this.dateError = this.$t('end-before-start')
            this.scrollToErrors()
            return
          }

          conditionResponse = await this.getConditionResponse()

          if (conditionResponse) {
            this.hasResponse = true
            let responseAttachments

            if (this.conditionAttachments.length > 0) {
              responseAttachments = (
                await conditionService.attachments(
                  this.conditionAttachments,
                  conditionResponse.hr_id,
                  'condition'
                )
              ).data

              console.warn(responseAttachments)
            }
          }
        } else {
          if (
            this.symptomForm.start &&
            isBeforeDate(
              this.symptomForm.start,
              new Date().toISOString().split('T')[0]
            )
          ) {
            this.dateError = this.$t('start-after-today')
            this.scrollToErrors()
            return
          }
          if (
            this.symptomForm.start &&
            this.symptomForm.end &&
            isBeforeDate(this.symptomForm.start, this.symptomForm.end)
          ) {
            this.dateError = this.$t('end-before-start')
            this.scrollToErrors()
            return
          }

          symptomResponse = await this.getSymptomResponse()

          if (symptomResponse) {
            this.hasResponse = true
            let responseAttachmentsSymptom

            if (this.symptomAttachments.length > 0) {
              responseAttachmentsSymptom = (
                await symptomService.attachments(
                  this.symptomAttachments,
                  symptomResponse.hr_id,
                  'symptom'
                )
              ).data
              console.warn(responseAttachmentsSymptom)
            }
          }
        }
        if (conditionResponse || symptomResponse) {
          await this.fetchWizardData()

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(item) {
      if (this.row === 'condition') {
        this.itemToConditionForm(item)
      } else {
        this.itemToSymptomForm(item)
      }
    },
    itemToConditionForm(item) {
      if (item.record.start) {
        this.conditionForm.start = item.record.start
      }
      this.conditionForm.start_date_part = item.record.start_date_part
      if (item.record.end) {
        this.conditionForm.end = item.record.end
      }
      this.conditionForm.end_date_part = item.record.end_date_part
      this.conditionForm.condition = item.record.condition
      this.conditionForm.verification_status =
        item.record.verification_status?.key
      this.conditionForm.note = item.note

      this.conditionForm.private = item.private
      this.originalConditionAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
    itemToSymptomForm(item) {
      if (item.record.start) {
        this.symptomForm.start = item.record.start
      }
      this.symptomForm.start_date_part = item.record.start_date_part
      if (item.record.end) {
        this.symptomForm.end = item.record.end
      }
      this.symptomForm.end_date_part = item.record.end_date_part
      this.symptomForm.symptom = item.record.symptom
      this.symptomForm.intensity = item.record.intensity
      this.symptomForm.frequency = item.record.frequency?.key
      this.symptomForm.interval = item.record.interval?.key
      this.symptomForm.note = item.note
      this.symptomForm.private = item.private
      this.originalSymptomAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1"
      justify="center"
    >
      <div class="mr-4">
        <v-img
          :src="
            require('@assets/images/safehaven/health-record-icons/symptom-check.svg')
          "
          height="30"
          width="30"
          contain
        ></v-img>
      </div>
      {{ $t('health-record.symptomcheaker.title') }}
    </v-row>
    <v-row class="mt-10">
      <v-col cols="12" md="6">
        <v-row
          :class="$style.hrQuestion"
          class="my-auto offblack--text"
          justify="center"
          no-gutters
        >
          Is this a condition or a symptom?
        </v-row>
      </v-col>
      <v-col cols="12" md="6">
        <v-radio-group
          :key="'radio-1'"
          v-model="row"
          row
          :disabled="edit || show"
          class="my-auto"
          :class="$style.hrRadio"
        >
          <v-row class="my-auto" justify-md="start" justify="center">
            <v-radio
              :label="$t('health-record.conditions.condition')"
              value="condition"
            ></v-radio>
            <v-radio
              :label="$t('health-record.conditions.symptom')"
              value="symptom"
            ></v-radio>
          </v-row>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-fade-transition mode="out-in">
      <div v-if="row === 'condition'">
        <v-row
          class="mt-10 offblack--text"
          :class="$style.hrQuestionFull"
          no-gutters
          justify="center"
          >Tell us your medical problem
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div :class="$style.hrComboBoxContainer">
            <ComboBox
              :key="'condition-combo-1'"
              :disabled="show"
              :combo-class="'condition-combo-1'"
              :value.sync="conditionForm.condition"
              :placeholder="$t('health-record.conditions.condition')"
              :error-messages="
                conditionForm.errors.has('condition')
                  ? 'Condition: ' + conditionForm.errors.get('condition')
                  : ''
              "
              fetch-url="/api/v1/hr/choices/condition/"
              rounded
              filled
              background-color="input"
              :class="$style.hrComboBox"
              @keydown="
                clearFormFromErrors(
                  'condition',
                  'conditionForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'conditionError'
                )
              "
            ></ComboBox>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >How long have you been experiencing this condition?
          </div>
        </v-row>
        <v-row v-if="dateError" class="ml-1">
          <v-col cols="12" md="12">
            <p class="error--text">{{ dateError }}</p>
          </v-col>
        </v-row>
        <v-row class="mt-10">
          <v-col cols="12" md="6">
            <div
              :class="
                mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
              "
            >
              <MedicalDatePickerDialog
                :key="'conditionDate-1'"
                :disabled="show"
                :dialog.sync="medicalDateStartPickerDialog"
                :title="'health-record.conditions.start-date'"
                :field="conditionForm.start"
                :part="conditionForm.start_date_part"
                filled
                background-color="input"
                :error-messages="
                  conditionForm.errors.has('start')
                    ? 'Start Date: ' + conditionForm.errors.get('start')
                    : ''
                "
                @input="
                  clearErrors(
                    'start',
                    'conditionForm',
                    // eslint-disable-next-line vue/comma-dangle
                    'conditionError'
                  )
                "
                @setMedicalDate="setMedicalDate"
                @showMedicalDate="showMedicalDate('start')"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div
              :class="
                mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
              "
            >
              <MedicalDatePickerDialog
                :key="'conditionDate-2'"
                :disabled="show"
                :dialog.sync="medicalDateEndPickerDialog"
                :title="'health-record.conditions.end-date'"
                :field="conditionForm.end"
                :part="conditionForm.end_date_part"
                filled
                background-color="input"
                @setMedicalDate="setMedicalDate"
                @showMedicalDate="showMedicalDate('end')"
                @input="dateError = null"
              />
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-7">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >Has this condition been verified by a healthcare practitioner?
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <v-autocomplete
              v-model="conditionForm.verification_status"
              :disabled="show"
              class="conditionVerificationStatus"
              attach=".conditionVerificationStatus"
              :items="verificationStatus"
              item-text="value"
              item-value="key"
              :error-messages="
                conditionForm.errors.has('verification_status')
                  ? 'Verification Status: ' +
                    conditionForm.errors.errors.verification_status
                  : ''
              "
              :placeholder="$t('health-record.conditions.verification-status')"
              rounded
              clearable
              filled
              background-color="input"
              :class="$style.hrAutoComplete"
              @change="
                clearFormFromErrors(
                  'verification_status',
                  'conditionForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'conditionError'
                )
              "
            ></v-autocomplete>
          </div>
        </v-row>

        <v-row class="mt-7">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >Any additional files or notes you'd like to include?
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <SpeechToText
              :key="'condition-2'"
              :value.sync="conditionForm.note"
              auto-grow
              rounded
              :class="$style.hrSpeechToText"
              :placeholder="$t('health-record.conditions.note')"
            ></SpeechToText>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <FileUpload
              :key="'condition-3'"
              :disabled="show"
              icon
              :height="200"
              :multiple="true"
              :files.sync="conditionAttachments"
              :non-draggable="true"
            ></FileUpload>
          </div>
        </v-row>

        <v-row v-if="edit" class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <AttachmentList
              :key="'attachment-list-1'"
              :disabled="show"
              :attachments="originalConditionAttachments"
              @delete-attachment="deleteAttachmentCondition"
            ></AttachmentList>
          </div>
        </v-row>
      </div>
      <div v-if="row === 'symptom'">
        <v-row class="mt-10">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >What's bothering you?
          </div>
        </v-row>
        <v-row class="mt-10" no-gutters justify="center">
          <div :class="$style.hrComboBoxContainer">
            <ComboBox
              :key="'symptom-combo-1'"
              :disabled="show"
              :combo-class="'symptom-combo-1'"
              :value.sync="symptomForm.symptom"
              :placeholder="$t('health-record.conditions.symptom')"
              :error-messages="
                symptomForm.errors.has('symptom')
                  ? 'Symptom: ' + symptomForm.errors.get('symptom')
                  : ''
              "
              fetch-url="/api/v1/hr/choices/symptom/"
              rounded
              filled
              background-color="input"
              :class="$style.hrComboBox"
              @keydown="
                clearFormFromErrors(
                  'symptom',
                  'symptomForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'conditionError'
                )
              "
            ></ComboBox>
          </div>
        </v-row>

        <v-row class="mt-10">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >How long have you been experiencing this symptom?
          </div>
        </v-row>
        <v-row v-if="dateError" class="ml-1">
          <v-col cols="12" md="12">
            <p class="error--text">{{ dateError }}</p>
          </v-col>
        </v-row>
        <v-row class="mt-10">
          <v-col cols="12" md="6">
            <div
              :class="
                mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
              "
            >
              <MedicalDatePickerDialog
                :key="'symptomDate-1'"
                :disabled="show"
                :dialog.sync="medicalDateStartPickerDialog"
                :title="'health-record.conditions.start-date'"
                :field="symptomForm.start"
                :part="symptomForm.start_date_part"
                filled
                background-color="input"
                :error-messages="
                  conditionForm.errors.has('start')
                    ? 'Start Date: ' + symptomForm.errors.get('start')
                    : ''
                "
                @input="
                  clearErrors(
                    'start',
                    'symptomForm',
                    // eslint-disable-next-line vue/comma-dangle
                    'conditionError'
                  )
                "
                @setMedicalDate="setMedicalDate"
                @showMedicalDate="showMedicalDate('start')"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div
              :class="
                mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
              "
            >
              <MedicalDatePickerDialog
                :key="'symptomDate-2'"
                :disabled="show"
                :dialog.sync="medicalDateEndPickerDialog"
                :title="'health-record.conditions.end-date'"
                :field="symptomForm.end"
                :part="symptomForm.end_date_part"
                filled
                background-color="input"
                @setMedicalDate="setMedicalDate"
                @showMedicalDate="showMedicalDate('end')"
                @input="dateError = null"
              />
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-7">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >How often does it occur?
          </div>
        </v-row>
        <v-row class="mt-10">
          <v-col cols="12" md="6">
            <div
              class="mx-auto"
              :class="mobile ? $style.hrInputDivMobile : $style.hrInputDiv"
            >
              <v-select
                v-model="symptomForm.frequency"
                :disabled="show"
                class="symptomFrequency"
                attach=".symptomFrequency"
                :items="frequencies"
                item-text="value"
                item-value="key"
                placeholder="Select Frequency"
                clearable
                rounded
                filled
                background-color="input"
                :class="$style.hrSelect"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div
              class="mx-auto"
              :class="mobile ? $style.hrInputDivMobile : $style.hrInputDiv"
            >
              <v-autocomplete
                v-model="symptomForm.interval"
                :disabled="show"
                class="symptomInterval"
                attach=".symptomInterval"
                :items="intervals"
                item-text="value"
                item-value="key"
                :placeholder="$t('health-record.conditions.interval')"
                rounded
                clearable
                filled
                background-color="input"
                :class="$style.hrAutoComplete"
              ></v-autocomplete>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-7">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >How bad is the intensity?
          </div>
        </v-row>
        <v-row class="mt-15">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <ColoredRangeSlider
              v-model="symptomForm.intensity"
              :disabled="show"
              :min="symptomIntensityMin"
              :max="symptomIntensityMax"
              :step="1"
              class="my-15"
            ></ColoredRangeSlider>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div :class="$style.hrQuestionFull" class="offblack--text"
            >Any additional files or notes you'd like to include?
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <SpeechToText
              :key="'symptom-2'"
              :disabled="show"
              :value.sync="symptomForm.note"
              auto-grow
              rounded
              :class="$style.hrSpeechToText"
              :placeholder="$t('health-record.conditions.note')"
            ></SpeechToText>
          </div>
        </v-row>
        <v-row class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <FileUpload
              :key="'symptom-3'"
              :disabled="show"
              icon
              :height="200"
              :multiple="true"
              :files.sync="symptomAttachments"
              :non-draggable="true"
            ></FileUpload>
          </div>
        </v-row>

        <v-row v-if="edit" class="mt-10">
          <div class="mx-auto" :class="$style.hrInputDiv">
            <AttachmentList
              :key="'attachment-list-2'"
              :disabled="show"
              :attachments="originalSymptomAttachments"
              @delete-attachment="deleteAttachmentSymptom"
            ></AttachmentList>
          </div>
        </v-row>
      </div>
    </v-fade-transition>

    <div v-if="row !== ''">
      <ForMyEyesOnly v-if="!show" :value.sync="computedRecord" />

      <HrActions
        :can-save="canSaveCondition || canSaveSymptom"
        :is-another="isAnother"
        :is-continue="isContinue"
        :trying-to-save="tryingToSave"
        :trying-to-delete="tryingToDelete"
        :edit="edit"
        @go-back="goBack"
        @try-to-save="tryToSave"
        @try-to-delete="prepareDeletion"
      />
    </div>

    <v-row v-if="conditionError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{ $t('health-record.conditions.error') }}</p>
        <div v-if="conditionForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in conditionForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
        <div v-if="symptomForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in symptomForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{ $t('health-record.conditions.message') }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.hrHeader {
  // styleName: Desktop/Display X-Small Bold;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}
.hrComboBox {
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  background: $color-theme-light-input;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrInputDivMobile {
  width: 60%;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.hrButton {
  border-radius: 8px !important;
}
.medicalDateDiv {
  width: 394px;
  margin-right: auto;
  margin-left: auto;
}
.medicalDateDivMobile {
  width: 60%;
  margin-right: auto;
  margin-left: auto;
}
</style>
